import { useTranslation } from 'react-i18next'

// The social auth endpoints live at the backend. Since Vite proxies `/api` →
// backend in dev, and nginx does the same in production, we can always use
// `/api` as the base for these full-page browser redirects.
const API_BASE = import.meta.env.VITE_API_URL || '/api'
const TWITCH_ENABLED = import.meta.env.VITE_TWITCH_ENABLE === 'true'

// ── SVG brand icons ───────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
)

const TwitchIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="currentColor" aria-hidden="true">
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0 1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

interface SocialLoginButtonsProps {
  mode?: 'login' | 'register'
}

/**
 * OAuth / SSO button pair (Twitch + Google).
 * Clicking either button triggers a full-page redirect to the backend's OAuth
 * authorisation endpoint. After the OAuth round-trip the backend redirects to
 * `/oauth-callback` with a JWT token in the query string.
 */
export function SocialLoginButtons({ mode = 'login' }: SocialLoginButtonsProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3">
      {/* Divider */}
      <div className="relative flex items-center">
        <div className="flex-1 border-t border-border" />
        <span className="mx-3 text-xs text-muted-foreground whitespace-nowrap">
          {t('auth.social_or')}
        </span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* Social buttons */}
      <div className="grid grid-cols-1 gap-2">
        {TWITCH_ENABLED && (
          <button
            type="button"
            onClick={() => {
              window.location.href = `${API_BASE}/auth/social/twitch?mode=${mode}`
            }}
            className="flex items-center justify-center gap-3 w-full rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <TwitchIcon />
            {t('auth.social_twitch')}
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            window.location.href = `${API_BASE}/auth/social/google?mode=${mode}`
          }}
          className="flex items-center justify-center gap-3 w-full rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <GoogleIcon />
          {t('auth.social_google')}
        </button>
      </div>
    </div>
  )
}
